import React, { useEffect, useState } from 'react';
import { Zap } from 'lucide-react';
import { useSocket } from '../hooks/useSocket';

// The signature meter: crowd engagement as a number the party can pump up.
// Computed honestly from real events (joins, votes, adds, reactions) with
// a 3-minute decay — go quiet and the energy drains.
const WINDOW_MS = 3 * 60 * 1000;

const tierFor = (energy) => {
  if (energy >= 75) return { label: 'Absolutely Lit', emoji: '🔥' };
  if (energy >= 50) return { label: 'Vibing', emoji: '🎉' };
  if (energy >= 25) return { label: 'Getting Going', emoji: '🎶' };
  return { label: 'Warming Up', emoji: '❄️' };
};

const PartyEnergyMeter = () => {
  const { energyEvents } = useSocket();
  const [energy, setEnergy] = useState(0);

  useEffect(() => {
    const compute = () => {
      const now = Date.now();
      let score = 0;
      (energyEvents || []).forEach(({ ts, w }) => {
        const age = now - ts;
        if (age < WINDOW_MS) {
          score += w * (1 - age / WINDOW_MS);
        }
      });
      setEnergy(Math.min(100, Math.round(score)));
    };
    compute();
    const id = setInterval(compute, 5000);
    return () => clearInterval(id);
  }, [energyEvents]);

  const tier = tierFor(energy);

  return (
    <div className="rounded-xl border border-white/10 bg-gray-800/70 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-300 flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5 text-yellow-400" />
          Party Energy
        </span>
        <span className="text-sm font-bold text-white">{energy}%</span>
      </div>

      <div className="relative h-2.5 rounded-full bg-gray-700 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 overflow-hidden rounded-full transition-all duration-700"
          style={{ width: `${Math.max(energy, 2)}%` }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: energy > 0 ? `${10000 / Math.max(energy, 2)}%` : '100%',
              background: 'linear-gradient(90deg, #F43F5E 0%, #FF6B00 25%, #FFC107 50%, #10B981 75%, #22D3EE 100%)'
            }}
          />
        </div>
      </div>

      <p className="text-xs text-gray-300 mt-2">
        {tier.emoji} {tier.label}
      </p>
    </div>
  );
};

export default PartyEnergyMeter;
